import { h } from "@stencil/core";
import { markdownToMdAST } from "@genexus/markdown-parser";
import { clearLinkDefinitions, getLinkDefinition, setLinkDefinition } from "./link-resolver";
/**
 * Regex to match the id of the heading.
 *
 * @input `"# Hello, world {#something} "`
 * @output `something`
 */
const HEADING_ID_REGEX = /\{#(.*?)\}/;
export const LAST_NESTED_CHILD_CLASS = "last-nested-child";
// Lazy load the code parser implementation
let codeToJSX;
let HTMLToJSX;
let lastNestedChild;
const checkAndGetLastNestedChildClass = (element) => element === lastNestedChild ? LAST_NESTED_CHILD_CLASS : undefined;
const depthToHeading = {
    1: (content, classes, id) => (h("h1", { class: classes, id: id }, content)),
    2: (content, classes, id) => (h("h2", { class: classes, id: id }, content)),
    3: (content, classes, id) => (h("h3", { class: classes, id: id }, content)),
    4: (content, classes, id) => (h("h4", { class: classes, id: id }, content)),
    5: (content, classes, id) => (h("h5", { class: classes, id: id }, content)),
    6: (content, classes, id) => (h("h6", { class: classes, id: id }, content))
};
const tableAlignmentDictionary = {
    left: "ch-markdown-table-column-start",
    center: "ch-markdown-table-column-center",
    right: "ch-markdown-table-column-end"
};
const tableRender = async (table, metadata) => {
    const tableHeadRow = table.children[0];
    const tableBodyRows = table.children.slice(1);
    const columnCount = tableHeadRow.children.length;
    // Head cell promises
    const headCellPromises = tableHeadRow.children.map(tableCell => mdASTtoJSX(tableCell, metadata));
    const bodyCellPromises = [];
    // Body cell promises
    tableBodyRows.forEach(tableHead => {
        tableHead.children.forEach(tableCell => {
            bodyCellPromises.push(mdASTtoJSX(tableCell, metadata));
        });
    });
    // Wait for all results to be completed in parallel
    const tableHeadContent = await Promise.allSettled(headCellPromises);
    const tableBodyContent = await Promise.allSettled(bodyCellPromises);
    // Return the JSX array
    const headCells = tableHeadContent.map(jsx => jsx.value);
    const bodyCells = tableBodyContent.map(jsx => jsx.value);
    const alignments = table.align.map(alignment => tableAlignmentDictionary[alignment]);
    return (h("table", null, h("thead", null, h("tr", null, tableHeadRow.children.map((tableCell, index) => (h("th", { class: {
            [alignments[index]]: !!alignments[index],
            [LAST_NESTED_CHILD_CLASS]: tableCell === lastNestedChild
        } }, headCells[index]))))), h("tbody", null, tableBodyRows.map((tableHead, rowIndex) => (h("tr", null, tableHead.children.map((tableCell, cellIndex) => (h("td", { class: {
            [alignments[cellIndex]]: !!alignments[cellIndex],
            [LAST_NESTED_CHILD_CLASS]: tableCell === lastNestedChild
        } }, bodyCells[columnCount * rowIndex + cellIndex])))))))));
};
export const renderDictionary = {
    blockquote: async (element, metadata) => {
        const content = await mdASTtoJSX(element, metadata);
        return (h("blockquote", { class: checkAndGetLastNestedChildClass(element) }, content));
    }, // TODO: Check if code can be inside this tag
    break: () => h("br", null),
    code: async (element, metadata) => {
        // Load the parser implementation
        codeToJSX ||= (await import("./code-highlight")).parseCodeToJSX; // TODO: Resolve race condition
        const content = await codeToJSX(element.value, element.lang, metadata.renderCode, element === lastNestedChild);
        return content;
    },
    definition: element => setLinkDefinition(element.identifier, element.url),
    delete: async (element, metadata) => {
        const content = await mdASTtoJSX(element, metadata);
        return (h("del", { class: checkAndGetLastNestedChildClass(element) }, content));
    }, // TODO: Check if code can be inside this tag
    emphasis: async (element, metadata) => {
        const content = await mdASTtoJSX(element, metadata);
        return h("em", { class: checkAndGetLastNestedChildClass(element) }, content);
    }, // TODO: Check if code can be inside this tag
    footnoteDefinition: () => "",
    footnoteReference: () => "",
    heading: async (element, metadata) => {
        // Check if the heading has an id
        const lastChild = element.children.at(-1);
        let headingId;
        if (lastChild?.type === "text") {
            const match = lastChild.value.match(HEADING_ID_REGEX);
            if (match && match.length > 1) {
                headingId = match[1];
                // Remove markdown id from the header text
                lastChild.value = lastChild.value.replace(`{#${headingId}}`, "");
            }
        }
        // Render the content after the heading id processing
        const content = await mdASTtoJSX(element, metadata);
        const classes = checkAndGetLastNestedChildClass(element);
        return depthToHeading[element.depth](content, classes, headingId); // TODO: Add anchor icon at the start of the heading
    },
    html: async (element, metadata) => {
        if (metadata.rawHTML && !HTMLToJSX) {
            // Load the parser implementation
            HTMLToJSX = (await import("./raw-html-to-jsx")).rawHTMLToJSX;
        }
        return metadata.rawHTML
            ? HTMLToJSX(element.value, metadata.allowDangerousHtml)
            : element.value;
    },
    image: element => (h("img", { src: element.url, alt: element.alt, title: element.title, loading: "lazy" })),
    imageReference: () => "",
    inlineCode: element => h("code", { class: "hljs" }, element.value),
    link: async (element, metadata) => {
        // Sanitize scripts
        if (element.url.includes("javascript:")) {
            return;
        }
        const content = await mdASTtoJSX(element, metadata);
        return (h("a", { "aria-label": element.title || null, title: element.title || null, class: checkAndGetLastNestedChildClass(element), href: element.url }, content));
    }, // TODO: Sanitize href?
    linkReference: async (element, metadata) => {
        const content = await mdASTtoJSX(element, metadata);
        let url = "";
        // TODO: Implement the rest of alternatives for "referenceType"
        if (element.referenceType === "shortcut") {
            url = await getLinkDefinition(element.identifier);
        }
        // TODO: It's unnecessary to set aria-label when referenceType === "shortcut"
        // TODO: The title is not supported well. See "An Example Putting the Parts Together" section in markdown.html
        // Sanitize scripts
        if (url.includes("javascript:")) {
            return;
        }
        return (h("a", { "aria-label": element.label || null, class: checkAndGetLastNestedChildClass(element), href: url }, content));
    },
    list: async (element, metadata) => {
        const content = await mdASTtoJSX(element, metadata);
        return element.ordered ? (h("ol", { class: checkAndGetLastNestedChildClass(element), start: element.start }, content) // TODO: Implement spread  // TODO: Check if code can be inside this tag
        ) : (h("ul", { class: checkAndGetLastNestedChildClass(element) }, content) // TODO: Implement spread  // TODO: Check if code can be inside this tag
        );
    },
    listItem: async (element, metadata) => {
        const content = await mdASTtoJSX(element, metadata);
        return h("li", { class: checkAndGetLastNestedChildClass(element) }, content);
    }, // TODO: Implement spread  // TODO: Check if code can be inside this tag
    paragraph: async (element, metadata) => {
        const content = await mdASTtoJSX(element, metadata);
        return h("p", { class: checkAndGetLastNestedChildClass(element) }, content);
    }, // TODO: Check if code can be inside this tag
    strong: async (element, metadata) => {
        const content = await mdASTtoJSX(element, metadata);
        return (h("strong", { class: checkAndGetLastNestedChildClass(element) }, content));
    }, // TODO: Check if code can be inside this tag
    table: tableRender, // TODO: Check if code can be inside this tag
    text: element => element.value,
    thematicBreak: () => h("hr", null),
    yaml: () => ""
};
const findLastNestedChild = (elementWithChildren) => {
    const lastChild = elementWithChildren.children.at(-1);
    // The last element have children. We must check its sub children
    if (lastChild.children?.length > 0) {
        return findLastNestedChild(lastChild);
    }
    if (lastChild.type === "code") {
        return lastChild;
    }
    return elementWithChildren;
};
/**
 * Converts markdown abstract syntax tree (mdast) into JSX.
 */
async function mdASTtoJSX(root, metadata) {
    const childrenLength = root.children.length;
    const asyncJSX = new Array(childrenLength);
    // Get the async JSX
    for (let index = 0; index < childrenLength; index++) {
        const child = root.children[index];
        asyncJSX.push(renderDictionary[child.type](child, metadata));
    }
    // Wait for all results to be completed in parallel
    const renderedContent = await Promise.allSettled(asyncJSX);
    // Return the JSX array
    return renderedContent.map(jsx => jsx.value);
}
export const markdownToJSX = async (markdown, metadata) => {
    const mdAST = markdownToMdAST(markdown);
    // First, find the last nested child. Useful to set a marker in the element
    // that accomplish this condition
    lastNestedChild = findLastNestedChild(mdAST);
    // Render the markdown as JSX
    const JSX = await mdASTtoJSX(mdAST, metadata);
    // Clear all definitions used to render the current markdown, so the next
    // render does not have old information
    clearLinkDefinitions();
    return JSX;
};
